import {
  calcPayrollLine, DEDUCTION_ITEMS, EARNING_ITEMS, EMPLOYMENT_LABEL, monthlyHours, PAYROLL_RATES, payrollDeduction, payrollGross, payrollMethods, payrollNet,
  type DeductionKey, type EarningKey, type EmploymentType, type PayrollLine,
} from '@selfon/domain/browser';
import { useEffect, useRef, useState } from 'react';
import { api, type PayrollMeta } from '../api';
import { Alert, Chip, Dialog, Empty, errorText, Field, Loading, PageHeader, useLoad, ViewDialog } from '../components/ui';
import { DateField } from '../components/DateField';
import { printElement } from '../components/print';
import './accounting.css';

/*
 * 급상여대장(원장)과 임금명세서(원장·직원 본인).
 * 원장이 입력하고 자동 계산을 눌러 확인한 뒤 저장·발행한다. 소득세는 간이세액표를 보고 입력한다.
 */

const nf = (v: number) => v.toLocaleString('ko-KR');
const monthTitle = (m: string) => `${m.slice(0, 4)}년 ${Number(m.slice(5, 7))}월`;
const dotted = (d: string) => (d ? d.replaceAll('-', '.') : '');
const LEDGER_EARNINGS: EarningKey[] = ['base', 'bonus', 'incentive', 'leave', 'meal', 'car', 'night'];
const LEDGER_DEDUCTIONS: DeductionKey[] = ['pension', 'health', 'care', 'employment', 'incomeTax', 'localTax', 'other'];
const label = (items: ReadonlyArray<readonly [string, string]>, k: string) => items.find(([i]) => i === k)![1];

/** 원장: 결산 회계 세무의 급상여대장 탭 */
export function PayrollLedger({ centerId, centerName, month }: { centerId: string; centerName: string; month: string }) {
  const q = useLoad(() => api.payroll(centerId, month), [centerId, month]);
  const [book, setBook] = useState<PayrollMeta | null>(null);
  const [lines, setLines] = useState<PayrollLine[]>([]);
  const [payDate, setPayDate] = useState('');
  const [editing, setEditing] = useState<PayrollLine | null>(null);
  const [viewing, setViewing] = useState<PayrollLine | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const ledgerRef = useRef<HTMLDivElement>(null);
  // 마지막으로 저장(또는 불러온) 내용. 지금 화면과 다르면 저장하지 않은 입력이 있는 것이다
  const [savedSnap, setSavedSnap] = useState('');
  const snap = JSON.stringify({ payDate, lines });
  const dirty = Boolean(savedSnap) && snap !== savedSnap;
  useEffect(() => {
    if (!q.data) return;
    setBook(q.data.book); setLines(q.data.lines); setPayDate(q.data.book?.payDate ?? ''); setNotice(null); setError(null);
    setSavedSnap(JSON.stringify({ payDate: q.data.book?.payDate ?? '', lines: q.data.lines }));
  }, [q.data]);
  // 저장하지 않은 입력이 있으면 창을 닫거나 새로고침할 때 브라우저가 한 번 더 묻는다
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const issued = book?.status === 'ISSUED';
  const version = book?.version ?? 0;
  const apply = (r: { book: PayrollMeta; lines: PayrollLine[] }) => { setBook(r.book); setLines(r.lines); setPayDate(r.book.payDate); setSavedSnap(JSON.stringify({ payDate: r.book.payDate, lines: r.lines })); };
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null); setNotice(null);
    try { await fn(); } catch (e) { setError(errorText(e)); }
    setBusy(false);
  };
  const save = () => run(async () => { apply(await api.savePayroll(centerId, month, { payDate, lines, expectedVersion: version })); setNotice('저장했습니다.'); });
  const issue = () => run(async () => {
    const r = await api.issuePayroll(centerId, month, version);
    apply(r);
    const m = r.mailed;
    setNotice(`발행했습니다. 직원이 내 임금명세서에서 볼 수 있습니다. 알림 메일 ${m.sent}건 전송${m.notConnected ? `, 메일 미연결 ${m.notConnected}건` : ''}${m.failed ? `, 실패 ${m.failed}건` : ''}${m.noEmail ? `, 이메일 없음 ${m.noEmail}건` : ''}.`);
  });
  const reopen = () => run(async () => { apply(await api.reopenPayroll(centerId, month, version)); setNotice('발행을 취소했습니다. 직원 화면에서 내려갔습니다.'); });
  const total = (pick: (l: PayrollLine) => number) => lines.reduce((a, l) => a + pick(l), 0);

  if (q.error) return <Alert>{q.error}</Alert>;
  if (!q.data) return <Loading />;
  return (
    <section className="stack payroll-section" aria-labelledby="payroll-title">
      <div className="panel-head no-print">
        <h2 id="payroll-title">{monthTitle(month)}분 급상여대장</h2>
        <Chip tone={issued ? 'ok' : 'muted'}>{issued ? '발행함' : book ? '작성 중' : '저장 전'}</Chip>
      </div>
      {error && <Alert>{error}</Alert>}
      {notice && <Alert kind="ok">{notice}</Alert>}
      {dirty && !issued && <Alert kind="info">저장하지 않은 입력이 있습니다. 저장을 눌러야 다음에 열어도 남습니다.</Alert>}
      {!lines.length && <Empty>직원 명부에 활동 중인 직원이 없습니다.</Empty>}
      <div className="row-actions no-print payroll-actions">
        <label className="payroll-date">지급일 <DateField label="지급일" value={payDate} disabled={issued} onChange={setPayDate} /></label>
        {!issued && <button type="button" className="btn btn-outline" disabled={busy} onClick={() => void save()}>저장</button>}
        {!issued && <button type="button" className="btn btn-primary" disabled={busy || !book} onClick={() => void issue()}>임금명세서 발행</button>}
        {issued && <button type="button" className="btn btn-outline" disabled={busy} onClick={() => void reopen()}>발행 취소</button>}
        <button type="button" className="btn btn-ghost" onClick={() => printElement(ledgerRef.current)}>대장 인쇄</button>
      </div>
      <p className="hint no-print">금액은 암호화해 저장합니다. 고친 뒤에는 저장해야 발행할 수 있습니다. 보험료율은 {PAYROLL_RATES.year}년 기준이고, 소득세는 간이세액표를 보고 입력합니다.</p>
      <div className="print-area" ref={ledgerRef}>
        <h2 className="print-only payroll-print-title">{monthTitle(month)}분 급상여대장</h2>
        <p className="print-only">회사명: {centerName} · 지급일: {dotted(payDate)}</p>
        <div className="table-wrap">
          <table className="table acct-table payroll-ledger">
            <caption className="sr-only">{monthTitle(month)}분 급상여대장</caption>
            <thead>
              <tr>
                <th scope="col">성명</th><th scope="col" className="no-print">작업</th><th scope="col">입사일</th><th scope="col">직위</th>
                {LEDGER_EARNINGS.map((k) => <th key={k} scope="col" className="num">{label(EARNING_ITEMS, k)}</th>)}
                <th scope="col" className="num">지급합계</th>
                {LEDGER_DEDUCTIONS.map((k) => <th key={k} scope="col" className="num">{label(DEDUCTION_ITEMS, k)}</th>)}
                <th scope="col" className="num">공제합계</th><th scope="col" className="num">차인지급액</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.userId}>
                  <th scope="row">{l.name}</th>
                  <td className="no-print payroll-row-actions">
                    <button type="button" className="btn btn-ghost btn-sm" disabled={issued} onClick={() => setEditing(l)}>입력</button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setViewing(l)}>명세서</button>
                  </td>
                  <td>{dotted(l.hireDate)}</td><td>{l.position}</td>
                  {LEDGER_EARNINGS.map((k) => <td key={k} className="num">{l.earnings[k] ? nf(l.earnings[k]) : ''}</td>)}
                  <td className="num"><strong>{nf(payrollGross(l))}</strong></td>
                  {LEDGER_DEDUCTIONS.map((k) => <td key={k} className="num">{l.deductions[k] ? nf(l.deductions[k]) : ''}</td>)}
                  <td className="num">{nf(payrollDeduction(l))}</td><td className="num"><strong>{nf(payrollNet(l))}</strong></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">인원 {lines.length}명</th><td className="no-print" /><td colSpan={2} />
                {LEDGER_EARNINGS.map((k) => <td key={k} className="num">{nf(total((l) => l.earnings[k]))}</td>)}
                <td className="num"><strong>{nf(total(payrollGross))}</strong></td>
                {LEDGER_DEDUCTIONS.map((k) => <td key={k} className="num">{nf(total((l) => l.deductions[k]))}</td>)}
                <td className="num">{nf(total(payrollDeduction))}</td><td className="num"><strong>{nf(total(payrollNet))}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      {editing && <LineDialog line={editing} onClose={() => setEditing(null)} onSave={(n) => { setLines(lines.map((l) => (l.userId === n.userId ? n : l))); setNotice(`${n.name}의 입력을 반영했습니다. 저장을 눌러 주세요.`); }} />}
      <ViewDialog wide title={viewing ? `${viewing.name} · ${monthTitle(month)}분 임금명세서` : '임금명세서'} open={Boolean(viewing)} onClose={() => setViewing(null)}
        actions={<button type="button" className="btn btn-outline" onClick={() => printElement(document.querySelector('dialog[open] .payslip'))}>인쇄·PDF 저장</button>}>
        {viewing && <Payslip line={viewing} month={month} payDate={payDate} centerName={centerName} />}
      </ViewDialog>
    </section>
  );
}

function LineDialog({ line, onClose, onSave }: { line: PayrollLine; onClose: () => void; onSave: (l: PayrollLine) => void }) {
  const [l, setL] = useState(line);
  const set = <K extends keyof PayrollLine>(k: K, v: PayrollLine[K]) => setL({ ...l, [k]: v });
  const num = (v: string) => (v === '' ? 0 : Number(v));
  const field = (k: keyof PayrollLine, name: string, type = 'text') => (
    <Field id={`pl-${k}`} label={name}>
      {type === 'date'
        ? <DateField id={`pl-${k}`} label={name} value={String(l[k] ?? '')} onChange={(v) => set(k, v as never)} />
        : <input id={`pl-${k}`} type={type} value={String(l[k] ?? '')} onChange={(e) => set(k, e.target.value as never)} />}
    </Field>
  );
  const count = (k: keyof PayrollLine, name: string) => (
    <Field id={`pl-${k}`} label={name}><input id={`pl-${k}`} type="number" min={0} step="any" value={Number(l[k]) || ''} onChange={(e) => set(k, num(e.target.value) as never)} /></Field>
  );
  const money = (group: 'earnings' | 'deductions', k: string, name: string) => (
    <Field key={k} id={`pl-${group}-${k}`} label={name}>
      <input id={`pl-${group}-${k}`} type="number" min={0} step={1} value={(l[group] as Record<string, number>)[k] || ''} onChange={(e) => setL({ ...l, [group]: { ...l[group], [k]: num(e.target.value) } })} />
    </Field>
  );
  return (
    <Dialog wide title={`${line.name} 급여 입력`} open onClose={onClose} submitLabel="반영" onSubmit={async () => onSave(l)}>
      <h3>인적 사항</h3>
      <div className="payroll-grid">
        {field('employeeNo', '사번')}{field('birthDate', '생년월일', 'date')}{field('email', '이메일', 'email')}{field('hireDate', '입사일', 'date')}{field('department', '부서')}{field('position', '직위')}
        <Field id="pl-employment" label="고용 형태">
          <select id="pl-employment" value={l.employment} onChange={(e) => set('employment', e.target.value as EmploymentType)}>
            {(Object.keys(EMPLOYMENT_LABEL) as EmploymentType[]).map((k) => <option key={k} value={k}>{EMPLOYMENT_LABEL[k]}</option>)}
          </select>
        </Field>
        {count('family', '가족 수')}
      </div>
      <h3>근로 시간</h3>
      <div className="payroll-grid">
        {count('weeklyHours', '주 근로시간')}{count('hourlyWage', '통상 시급(원)')}{count('workDays', '근로일수')}{count('workHours', '총 근로시간')}{count('overtimeHours', '연장근로시간')}{count('nightHours', '야간근로시간')}{count('holidayHours', '휴일근로시간')}{count('pensionBase', '국민연금 기준소득월액(원)')}
      </div>
      {l.hourlyWage > 0 && l.hourlyWage < PAYROLL_RATES.minHourlyWage && <Alert>시급이 {PAYROLL_RATES.year}년 최저임금 {nf(PAYROLL_RATES.minHourlyWage)}원보다 낮습니다.</Alert>}
      <h3>지급</h3>
      <div className="payroll-grid">{EARNING_ITEMS.map(([k, name]) => money('earnings', k, name))}</div>
      <h3>공제</h3>
      <div className="payroll-grid">{DEDUCTION_ITEMS.map(([k, name]) => money('deductions', k, name))}</div>
      <button type="button" className="btn btn-outline" onClick={() => setL(calcPayrollLine(l))}>기본급·4대 보험·지방소득세 자동 계산</button>
      <p className="muted small">지급 {nf(payrollGross(l))}원 − 공제 {nf(payrollDeduction(l))}원 = 실수령 {nf(payrollNet(l))}원{l.weeklyHours ? ` · 월 ${monthlyHours(l.weeklyHours)}시간` : ''}</p>
    </Dialog>
  );
}

/** 임금명세서 본문. 원장 미리보기와 직원 화면이 함께 쓴다. */
export function Payslip({ line: l, month, payDate, centerName }: { line: PayrollLine; month: string; payDate: string; centerName: string }) {
  const rows = Math.max(EARNING_ITEMS.length, DEDUCTION_ITEMS.length);
  const methods = payrollMethods(l);
  const cell = (v: number) => (v ? nf(v) : '');
  return (
    <article className="payslip print-area">
      <h2>{monthTitle(month)}분 임금명세서</h2>
      <dl className="payslip-head">
        <div><dt>회사명</dt><dd>{centerName}</dd></div><div><dt>입사일</dt><dd>{dotted(l.hireDate)}</dd></div>
        <div><dt>성명</dt><dd>{l.name}</dd></div><div><dt>생년월일(사번)</dt><dd>{dotted(l.birthDate)}{l.employeeNo ? ` (${l.employeeNo})` : ''}</dd></div>
        <div><dt>부서</dt><dd>{l.department}</dd></div><div><dt>직위</dt><dd>{l.position}</dd></div>
        <div><dt>지급일</dt><dd>{dotted(payDate)}</dd></div><div><dt>고용 형태</dt><dd>{EMPLOYMENT_LABEL[l.employment]}</dd></div>
      </dl>
      <table className="table payslip-table">
        <caption className="sr-only">지급과 공제 내역</caption>
        <thead><tr><th scope="col">임금 항목</th><th scope="col" className="num">지급 금액</th><th scope="col">공제 항목</th><th scope="col" className="num">공제 금액</th></tr></thead>
        <tbody>
          {Array.from({ length: rows }, (_, i) => {
            const e = EARNING_ITEMS[i]; const d = DEDUCTION_ITEMS[i];
            return <tr key={i}><td>{e?.[1]}</td><td className="num">{e && cell(l.earnings[e[0]])}</td><td>{d?.[1]}</td><td className="num">{d && cell(l.deductions[d[0]])}</td></tr>;
          })}
        </tbody>
        <tfoot>
          <tr><th scope="row">지급액 계</th><td className="num">{nf(payrollGross(l))}</td><th scope="row">공제액 계</th><td className="num">{nf(payrollDeduction(l))}</td></tr>
          <tr><td colSpan={2} /><th scope="row">실수령액</th><td className="num"><strong>{nf(payrollNet(l))}</strong></td></tr>
        </tfoot>
      </table>
      <ul className="payslip-work">
        <li>근로일수: {l.workDays}일</li><li>총 근로시간: {l.workHours}시간</li><li>연장근로시간: {l.overtimeHours || '-'}</li><li>야간근로시간: {l.nightHours || '-'}</li>
        <li>휴일근로시간: {l.holidayHours || '-'}</li><li>통상 시급: {l.hourlyWage ? `${nf(l.hourlyWage)}원` : '-'}</li><li>가족 수: {l.family}인</li>
      </ul>
      <table className="table payslip-table">
        <caption>계산 방법</caption>
        <thead><tr><th scope="col">항목</th><th scope="col">산출식 또는 산출 방법</th><th scope="col" className="num">금액</th></tr></thead>
        <tbody>{methods.map((m) => <tr key={m.item}><td>{m.item}</td><td>{m.method}</td><td className="num">{nf(m.amount)}</td></tr>)}</tbody>
      </table>
      <p>귀하의 노고에 감사드립니다.</p>
    </article>
  );
}

/** 직원: 발행된 내 임금명세서 */
export function MyPayslips() {
  const q = useLoad(() => api.myPayslips(), []);
  const [month, setMonth] = useState<string | null>(null);
  const open = useLoad(() => (month ? api.myPayslip(month) : Promise.resolve(null)), [month]);
  return (
    <div className="stack">
      <PageHeader title="내 임금명세서" sub="원장님이 발행한 명세서를 볼 수 있습니다." />
      {q.error && <Alert>{q.error}</Alert>}
      {q.loading && !q.data ? <Loading /> : !q.data?.payslips.length ? <Empty>아직 발행된 명세서가 없습니다.</Empty> : (
        <ul className="plain stack">
          {q.data.payslips.map((p) => (
            <li key={p.month} className="card payroll-my-row">
              <span><strong>{monthTitle(p.month)}분</strong> <span className="muted small">지급일 {dotted(p.payDate)}</span></span>
              <span>실수령 {nf(p.net)}원</span>
              <button type="button" className="btn btn-outline" onClick={() => setMonth(p.month)}>명세서 보기</button>
            </li>
          ))}
        </ul>
      )}
      <ViewDialog wide title={month ? `${monthTitle(month)}분 임금명세서` : '임금명세서'} open={Boolean(month)} onClose={() => setMonth(null)}
        actions={<button type="button" className="btn btn-outline" onClick={() => printElement(document.querySelector('dialog[open] .payslip'))}>인쇄·PDF 저장</button>}>
        {open.error && <Alert>{open.error}</Alert>}
        {open.data && <Payslip line={open.data.line} month={open.data.month} payDate={open.data.payDate} centerName={open.data.centerName} />}
      </ViewDialog>
    </div>
  );
}
